import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import AnimatedResident from '../AnimatedResident';
import SafeMount from '../SafeMount';
import { useCityStore } from '../../city/cityStore';
import { bandPose } from '../../city/stagePlan';
import { STAGE_Y } from '../../city/cityMotion';
import { baseGroundY } from '../../city/cityGround';
import { cutsceneBridge } from '../../city/cutscene';
import { serverNow } from '../../services/worldSocket';
import { requestLoad, releaseLoad, bodyGone, PREFETCH_RADIUS, LOAD_RADIUS, UNLOAD_RADIUS } from '../Npc';
import { ktx2Extend } from '../../utils/ktx2';

// StageBand — the band behind the lead (phase 2c, 2026-11-04).
//
// Khari, on band members: *"Wizard and stage only for now, but they might walk
// the city later."* A member with a body of their own (built in the wizard) is
// not a resident: they live nowhere in the city, so they are drawn only here, on
// the stage, for their band's set (API `shows[].band`), at a backup mark. Where
// they stand and what they do is city/stagePlan bandPose, from the show and the
// moment alone, so every screen agrees.
//
// The body streams in the same queue as every resident (one body at a time), by
// where the visitor stands, and goes when the set is over or they walk away.
// ⚠ THE DAY MEMBERS WALK THE CITY they become residents (CityResident), and a
// member would then be drawn twice: this list must leave residents out.

const hash = (n) => { const x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); };

export default function StageBand({ bodies, residentIds }) {
  const shows = useCityStore((s) => s.snap?.shows || null);
  // Everyone in a band on any stage set in the snapshot (the API sends sets up to 20 minutes ahead).
  const members = useMemo(() => {
    const out = new Map();
    for (const sh of shows || []) {
      if (sh.inside || !Array.isArray(sh.band)) continue;
      for (const m of sh.band) {
        const id = Number(m.a);
        if (!residentIds.has(id) && !out.has(id)) out.set(id, { id, name: m.name || '', place: sh.place });
      }
    }
    return [...out.values()];
  }, [shows, residentIds]);
  if (!members.length) return null;
  return (
    <group>
      {members.map((m) => <StageMember key={m.id} member={m} body={bodies.get(m.id) || null} />)}
    </group>
  );
}

function StageMember({ member, body }) {
  const avatarId = member.id;
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const group = useRef();
  const [move, setMove] = useState(null);
  const moveRef = useRef(null);
  const [on, setOn] = useState(false);       // in a set right now
  const onRef = useRef(false);
  const slug = `band-${avatarId}`;
  const [bodyOn, setBodyOn] = useState(false);
  const bodyOnRef = useRef(false);
  const queuedRef = useRef(false);
  const slotRef = useRef(false);
  const prefetched = useRef(false);
  const height = body?.height || 0.75;

  useFrame(() => {
    const g = group.current;
    if (!g) return;
    const cs = useCityStore.getState();
    const stage = (cs.snap?.places || []).find((p) => p.slug === member.place)?.stage || null;
    const pose = bandPose(cs.snap?.shows, avatarId, stage, serverNow());
    const live = !!pose;
    if (live !== onRef.current) { onRef.current = live; setOn(live); }
    g.visible = live;
    let standDist = Infinity;
    if (pose) {
      g.position.set(pose.x, baseGroundY(pose.x, pose.z) + STAGE_Y, pose.z);
      g.rotation.y = pose.heading;
      if (pose.move !== moveRef.current) { moveRef.current = pose.move; setMove(pose.move); }
      const me = (cutsceneBridge.active && cutsceneBridge.at) || camera.position;
      standDist = Math.hypot(me.x - pose.x, me.z - pose.z);
    }
    if (!prefetched.current && standDist < PREFETCH_RADIUS && body) {
      prefetched.current = true;
      for (const url of [body.rigged, body.walk, body.idle].filter(Boolean)) {
        try { useGLTF.preload(url, true, true, ktx2); } catch { /* best effort */ }
      }
    }
    const wantBody = live && !!body?.rigged && (bodyOnRef.current ? standDist < UNLOAD_RADIUS : standDist < LOAD_RADIUS);
    if (wantBody) {
      if (!bodyOnRef.current && !queuedRef.current) {
        queuedRef.current = true;
        requestLoad(slug, standDist, () => { slotRef.current = true; bodyOnRef.current = true; queuedRef.current = false; setBodyOn(true); });
      }
    } else if (bodyOnRef.current || queuedRef.current) {
      releaseLoad(slug, slotRef.current);
      bodyGone(slug);
      slotRef.current = false; queuedRef.current = false;
      if (bodyOnRef.current) { bodyOnRef.current = false; setBodyOn(false); }
    }
  });

  useEffect(() => () => { releaseLoad(slug, slotRef.current); bodyGone(slug); slotRef.current = false; }, [slug]);
  const onReady = useCallback(() => {
    if (!slotRef.current) return;
    slotRef.current = false;
    releaseLoad(slug, true);
  }, [slug]);

  return (
    <group ref={group} visible={false}>
      {on && bodyOn && body && (
        <SafeMount label={`band member ${member.name || avatarId}`} retryMs={30000} onRetry={() => useGLTF.clear([body.rigged, body.walk, body.idle].filter(Boolean))}>
          <Suspense fallback={null}>
            <AnimatedResident
              meshUrl={body.rigged}
              rigPose={body.pose || null}
              clipMap={body.clips || null}
              clipUrls={[body.walk, body.idle].filter(Boolean)}
              play="idle"
              activity={move}
              avatarId={avatarId}
              position={[0, 0, 0]}
              rotationY={0}
              height={height}
              phaseOffset={hash(avatarId + 1)}
              rate={0.9 + hash(avatarId + 2) * 0.2}
              onReady={onReady}
            />
          </Suspense>
        </SafeMount>
      )}
    </group>
  );
}
